export interface CaseStudy {
  title: string;
  background: string;
  task: string;
  discussion: string;
}

export interface Assignment {
  title: string;
  type: '个人作业' | '小组讨论' | 'Case Study' | '实战演练' | '分组报告';
  description: string;
  deliverable: string;
}

export interface CourseExtended {
  id: string;
  coverImage: string;
  expertQuote: string;
  expertName: string;
  expertTitle: string;
  learningObjectives: string[];
  keyPoints: string[];
  caseStudies: CaseStudy[];
  assignments: Assignment[];
  whoShouldTake: string[];
  courseIntro: string;
}

export const coursesExtended: CourseExtended[] = [
  {
    id: 'bc-fundamentals',
    coverImage: 'https://public.readdy.ai/ai/img_res/4be793e1f7d8541a4029dbebb563b1a8.jpg',
    expertQuote: 'Blockchain is to trust what the internet was to communication — a foundational technology that rewires how value moves across the world.',
    expertName: 'Don Tapscott',
    expertTitle: '《区块链革命》作者，全球区块链研究权威',
    courseIntro: '本课程以"零基础到真正理解"为核心目标，摒弃枯燥的技术文档，用现实类比和真实案例让你彻底搞懂区块链——从哈希函数到智能合约，从共识机制到联盟链部署。课程深度覆盖RWA行业必用的底层基础，每一节课都直指实战应用，是RWA从业者不可绕开的必修基础。',
    learningObjectives: [
      '理解区块链的核心价值主张：为什么它能替代"中间机构"建立信任',
      '掌握哈希函数、Merkle树、数字签名三大密码学基础工具',
      '透彻理解PoW/PoS/PBFT三种共识机制的经济与安全逻辑',
      '区分公链/联盟链/私链的核心差异，知道RWA应该用哪种',
      '理解EVM与智能合约的执行逻辑，为RWA合规合约开发打基础',
      '具备独立阅读区块链技术白皮书的能力',
    ],
    keyPoints: [
      '区块链本质：分布式账本与数据库的根本差异',
      '密码学三件套：哈希函数、非对称加密、数字签名',
      'Merkle树：高效验证数据完整性的数学结构',
      'PoW/PoS/PBFT：三大共识机制的原理、安全边界与成本对比',
      'EVM：以太坊通用计算引擎的工作逻辑',
      'Gas经济学：区块链激励机制与费用模型',
      '联盟链实战：蚂蚁链/长安链/FISCO BCOS的部署与应用',
      '区块链信息服务备案：中国合规联盟链的必要条件',
    ],
    caseStudies: [
      {
        title: 'Case Study 1：蚂蚁链供应链金融存证案例',
        background: '某大型零售商使用蚂蚁链将应收账款上链存证，供应商可据此向银行申请融资，彻底解决供应链融资信息不透明问题。',
        task: '分析该案例中蚂蚁链如何利用区块链的"不可篡改性"替代银行的信任背书，绘制完整的链上数据流向图。',
        discussion: '讨论：如果使用中心化数据库而非区块链，会带来哪些具体风险？区块链在此场景的不可替代价值是什么？',
      },
      {
        title: 'Case Study 2：以太坊PoS切换的历史经验与教训',
        background: '2022年以太坊完成"合并"（The Merge），从PoW切换至PoS，这是区块链史上最大规模的共识机制迁移，历时7年研发。',
        task: '对比PoW与PoS在安全性、能耗、中心化风险三个维度的表现，分析为何以太坊选择PoS而比特币坚持PoW。',
        discussion: '讨论：对于RWA底层链来说，联盟链的PBFT比公链的PoS更适合，还是反之？你的判断依据是什么？',
      },
    ],
    assignments: [
      {
        title: '区块链原理思维导图',
        type: '个人作业',
        description: '以"一张图说清楚区块链如何运作"为目标，绘制涵盖哈希、共识、智能合约、应用四个层次的完整思维导图，要求逻辑清晰、可独立解释给非技术背景的人听。',
        deliverable: '提交思维导图文件（PDF/PNG），附500字讲解说明',
      },
      {
        title: '公链 vs 联盟链选型分析报告',
        type: '小组讨论',
        description: '三人一组，针对指定的RWA场景（①商业地产收益权代币化，②供应链应收账款融资，③碳信用存证与交易）分别给出链型选择建议，并在课堂上进行10分钟陈述。',
        deliverable: 'PPT陈述（10分钟）+ 一页选型建议书',
      },
    ],
    whoShouldTake: [
      '零基础想系统了解区块链技术逻辑的RWA行业从业者',
      '需要与区块链技术团队有效沟通的业务、法律、合规人员',
      '正在评估是否布局RWA业务的企业管理层',
      '希望从事RWA行业但技术背景薄弱的金融从业者',
    ],
  },
  {
    id: 'erc-standards',
    coverImage: 'https://readdy.ai/api/search-image?query=ethereum%20ERC%20token%20standards%20textbook%20cover%20deep%20indigo%20purple%20digital%20circuit%20board%20smart%20contract%20code%20glowing%20lines%20holographic%20token%20symbols%20geometric%20hexagon%20professional%20futuristic%20book%20design&width=600&height=840&seq=cover_erc02&orientation=portrait',
    expertQuote: 'Token standards are the grammar of the blockchain economy. Without ERC standards, every protocol would be its own island — interoperability is what makes DeFi and RWA possible at scale.',
    expertName: 'Vitalik Buterin',
    expertTitle: '以太坊联合创始人',
    courseIntro: '代币标准是区块链金融体系的通用语言。本课程系统梳理从ERC-20到ERC-4626的完整以太坊代币标准谱系，重点精讲RWA行业最核心的ERC-1400、ERC-3643合规证券标准，以及碳资产最优的ERC-3525半同质化代币应用。无论你是产品设计者、合规顾问还是技术架构师，这门课都将让你对代币化的底层语言了然于胸。',
    learningObjectives: [
      '系统掌握22个主流ERC标准的分类、用途与适用场景',
      '深度理解ERC-1400/ERC-3643合规证券代币标准的完整架构',
      '掌握ERC-3525半同质化代币在碳资产场景的设计逻辑',
      '能够为不同类型的RWA资产独立设计代币化方案并选择最优ERC标准',
      '理解ERC-4626金库标准，为RWA收益池产品设计提供技术框架',
    ],
    keyPoints: [
      'ERC体系全景：同质化(FT)、非同质化(NFT)、合规专用三大类',
      'ERC-20深度：transfer/approve/allowance的状态机模型',
      'ERC-3643 T-REX协议：身份注册表+合规模块的完整架构',
      'ERC-1400安全代币标准：分区、强制转让、文档注册',
      'ERC-721A优化：批量铸造的Gas成本优化原理',
      'ERC-3525：碳配额分级与半同质化资产的最优表达',
      'ERC-4626金库标准：RWA收益聚合协议的标准实现',
      '选型决策树：资产类型×合规要求×流动性需求的综合判断',
    ],
    caseStudies: [
      {
        title: 'Case Study 1：BlackRock BUIDL如何选择代币标准',
        background: 'BlackRock BUIDL基金是全球最大的代币化基金，底层采用ERC-1400证券代币标准，结合白名单机制，仅允许经过KYC认证的合格投资者持有。',
        task: '分析BlackRock为何选择ERC-1400而非普通ERC-20，逐条对比两种标准在合规性、转让限制、监管报告三个维度的差异。',
        discussion: '讨论：如果RWA底层采用普通ERC-20，会带来哪些具体的监管合规风险？',
      },
      {
        title: 'Case Study 2：Toucan Protocol碳信用代币化设计解析',
        background: 'Toucan Protocol使用ERC-3525将碳信用按项目类型、年份、核证标准进行分层代币化，既保留了碳信用的唯一性，又实现了同类别碳信用的可互换性。',
        task: '绘制Toucan Protocol的代币化架构图，说明ERC-3525如何同时表达碳信用的"唯一性"（项目级）和"可换性"（同类级）两种属性。',
        discussion: '讨论：对于中国CCER碳信用，应该选用ERC-3525还是ERC-1155？你的选择依据是什么？',
      },
    ],
    assignments: [
      {
        title: 'RWA代币标准选型方案书',
        type: '实战演练',
        description: '从以下三个资产场景中选一个（①写字楼租金收益权，②光伏电站碳减排信用，③专利技术授权收益），设计完整的代币化方案，包括ERC标准选型、白名单机制设计、收益分配逻辑，以及二级市场流通设计。',
        deliverable: '方案书（2000字以内）+ 架构示意图',
      },
      {
        title: 'ERC代币标准横向对比',
        type: '个人作业',
        description: '制作ERC-20 vs ERC-1400 vs ERC-3643在"合规约束力、二级市场流动性、Gas成本、监管认可度"四个维度的对比矩阵，并给出你对"未来5年最主流RWA代币标准"的判断及依据。',
        deliverable: '对比矩阵表格 + 500字分析报告',
      },
    ],
    whoShouldTake: [
      'RWA项目产品经理和技术架构师',
      '合规律师和法律顾问，需要理解代币化的技术底层',
      '区块链开发者，希望进入RWA赛道',
      '数字资产领域的投资分析师',
    ],
  },
  {
    id: 'rwa-fundamentals',
    coverImage: 'https://readdy.ai/api/search-image?query=RWA%20real%20world%20asset%20tokenization%20textbook%20cover%20deep%20crimson%20red%20dark%20background%20glowing%20city%20skyline%20digital%20tokens%20floating%20buildings%20real%20estate%20blockchain%20bridge%20physical%20digital%20world%20professional%20futuristic%20book%20design&width=600&height=840&seq=cover_rwa03&orientation=portrait',
    expertQuote: 'Tokenizing real-world assets is not just about digitizing paper — it is about fundamentally reimagining how trillions of dollars of value can flow more freely, more transparently, and more efficiently.',
    expertName: 'Larry Fink',
    expertTitle: 'BlackRock首席执行官',
    courseIntro: 'RWA是当前全球金融市场最重要的结构性机遇。本课程从"RWA是什么"开始，逐步深入到资产类型全图谱、发行完整流程、投资风险管理，是市场上覆盖最完整的RWA体系化入门课程。课程结合Blackrock、Centrifuge、RealT等真实项目案例，确保你不只是学到理论，而是能够真正评估、参与和落地RWA项目。',
    learningObjectives: [
      '掌握RWA的完整定义、历史演进路径与当前市场规模数据',
      '能够区分并深度理解6大主流RWA资产类型的价值主张与风险特征',
      '熟悉RWA发行全链路5大步骤，从SPV搭建到代币上线的完整流程',
      '建立RWA投资分析框架：底层资产质量、智能合约风险、流动性风险三维评估',
      '能够独立对一个RWA项目进行基础尽职调查并输出投资意见',
    ],
    keyPoints: [
      'RWA定义与历史：从ABS→STO→RWA的金融创新演进脉络',
      'RWA五大价值主张：流动性、可及性、透明度、效率、可编程收益',
      '六大资产类型：国债/私募信贷/房产/碳信用/贵金属/知识产权',
      'SPV结构：开曼/BVI/香港SPV搭建的法律逻辑与成本对比',
      'KYC/AML合规系统：白名单机制与投资者适当性管理',
      'RWA四大风险：智能合约/流动性/法律/信用',
      '全球头部平台对比：Ondo/Centrifuge/Maple/BlackRock BUIDL',
      '未来展望：350亿→万亿美元的增长驱动因素',
    ],
    caseStudies: [
      {
        title: 'Case Study 1：BlackRock BUIDL — 华尔街如何进入RWA',
        background: 'BlackRock于2024年3月推出BUIDL基金，以美国国债为底层资产，采用Securitize平台发行，AUM在18个月内突破120亿美元，成为史上规模最大的代币化基金。',
        task: '拆解BUIDL的完整架构：发行方（BlackRock）、托管方（BNY Mellon）、代币化平台（Securitize）、智能合约标准（ERC-1400）各自的角色与责任边界。分析BUIDL成功的5个关键因素。',
        discussion: '讨论：BUIDL采用许可链（白名单合格投资者），而非完全公开流通。这对RWA的"普惠金融"目标是支持还是矛盾？',
      },
      {
        title: 'Case Study 2：Centrifuge与DeFi的深度整合',
        background: 'Centrifuge将中小企业应收账款RWA接入MakerDAO和Aave，让链上流动性直接为链下实体融资，是迄今最成功的RWA×DeFi整合案例，累计为中小企业融资超5亿美元。',
        task: '梳理Centrifuge的"真实出售→链上代币→DeFi抵押→链下还款"全链路，识别每个环节的核心风险点，并设计对应的风险缓释措施。',
        discussion: '分组讨论：如果一个中国中小企业想通过类似Centrifuge的模式进行RWA融资，应该从哪里开始？面临哪些本地化合规挑战？',
      },
    ],
    assignments: [
      {
        title: 'RWA项目尽职调查报告',
        type: 'Case Study',
        description: '从公开信息中选择一个真实的RWA项目（Ondo OUSG/Maple Finance/RealT/Toucan等），对其进行独立尽职调查，涵盖底层资产质量、合规架构、智能合约安全性、流动性机制和风险揭示五个维度，最终给出"是否推荐投资"的明确判断。',
        deliverable: '尽职调查报告（3000字）+ 投资评级（A/B/C/D）及理由',
      },
      {
        title: 'RWA赛道研究报告',
        type: '分组报告',
        description: '四人一组，选择一个RWA资产赛道（碳资产/私募信贷/商业地产/知识产权），深度研究该赛道全球市场现状、主流平台格局、监管框架、未来增长预测，课堂呈现15分钟，并回答评委提问。',
        deliverable: 'PPT演示（15分钟）+ 研究报告摘要（500字）',
      },
    ],
    whoShouldTake: [
      '金融机构、资产管理公司正在研究RWA布局的专业人士',
      '希望系统了解RWA全貌的投资者',
      '正在考虑将资产代币化的实体企业决策层',
      '法律、审计、咨询机构中面向RWA客户提供服务的从业者',
    ],
  },
  {
    id: 'hk-compliance',
    coverImage: 'https://readdy.ai/api/search-image?query=Hong%20Kong%20RWA%20compliance%20SFC%20regulation%20textbook%20cover%20dark%20amber%20golden%20Hong%20Kong%20skyline%20night%20harbor%20blockchain%20legal%20scales%20document%20holographic%20professional%20futuristic%20book%20cover%20design&width=600&height=840&seq=cover_hk04&orientation=portrait',
    expertQuote: "Hong Kong's regulatory approach to tokenised assets demonstrates a sophisticated understanding: innovation thrives not in regulatory vacuums, but within frameworks that balance investor protection with market development.",
    expertName: 'Julia Leung',
    expertTitle: '香港证监会（SFC）行政总裁',
    courseIntro: '香港是中国资产走向全球最重要的合规门户。本课程是市场上最系统、最深入的香港RWA合规专项课程，完整覆盖SFC监管框架、三类核心牌照要求、KYC/AML系统搭建、跨境发行实战流程，以及香港与内地双向合规衔接机制。每一条规则的背后都有清鉴元衡的实战解读，确保你学到的是真正可以落地的合规知识，而非脱离实际的条文背诵。',
    learningObjectives: [
      '系统掌握香港SFC针对RWA的完整监管框架与法规体系',
      '熟悉1号/4号/9号三类核心牌照的申请条件、流程与时间成本',
      '能够独立设计符合香港合规要求的KYC/AML系统架构',
      '理解香港FATF旅行规则对RWA跨境转让的影响与合规方案',
      '掌握内地资产赴港RWA发行的完整合规通道与风险控制',
    ],
    keyPoints: [
      'SFC/HKMA/FSTB三机构分工与协作机制',
      'VASP发牌制度：条件、费用、时间线全解析',
      'SFC八号通函逐条精读与RWA实务影响',
      '证券型代币判断标准：Howey Test的香港版应用',
      'KYC七步法：文件收集→风险分级→持续监控',
      'AML系统：制裁名单筛查+可疑交易报告+FATF旅行规则',
      '专业投资者认定：香港PI标准与内地合格投资者的差异',
      '香港与内地合规衔接：存证互认机制与联合监管框架',
    ],
    caseStudies: [
      {
        title: 'Case Study 1：德林大厦物业RWA——香港首个合规商业地产RWA',
        background: '2026年，德林控股将香港中环德林大厦写字楼收益权通过RWA方式代币化，获SFC无异议函，由HashKey持牌机构担任托管方，向香港专业投资者开放认购，最低认购额远低于传统地产投资。',
        task: '从合规架构角度，分析德林大厦项目如何满足SFC的"底层资产尽职调查+投资者适当性管理+持续信息披露"三大核心要求，绘制完整的合规架构图。',
        discussion: '讨论：如果你是德林大厦项目的合规顾问，在申请SFC无异议函的过程中，你会重点关注哪三个潜在的合规风险点？如何提前规避？',
      },
      {
        title: 'Case Study 2：HashKey持牌平台合规实践解析',
        background: 'HashKey Exchange是香港首批获得SFC 1号牌和7号牌的持牌交易所之一，其在RWA代币上市审查方面建立了一套完整的评估标准。',
        task: '研究HashKey的项目审查框架，梳理其评估一个RWA代币是否符合上市条件的核心维度（底层资产、合规文件、技术审计、信息披露），并与传统证券交易所的上市条件进行对比。',
        discussion: '分组讨论：内地某光伏企业希望在HashKey上市其碳资产RWA代币，你作为合规顾问，准备哪些材料，可能面临哪些拒绝风险？',
      },
    ],
    assignments: [
      {
        title: '香港RWA合规方案设计',
        type: '实战演练',
        description: '为一家内地大型房企设计完整的赴港RWA发行合规方案，包括：SPV架构选择、SFC牌照路径规划、KYC/AML系统方案、专业投资者管理方案、信息披露计划，以及预计时间与成本估算。',
        deliverable: '合规方案书（5页）+ 时间成本估算表',
      },
      {
        title: 'SFC条例核心条款精读测验',
        type: '个人作业',
        description: '阅读课程提供的SFC八号通函与证监会2024年RWA指引（中文版），回答10道判断与分析题，包括具体条款对特定业务行为的影响判断。',
        deliverable: '作答文档提交',
      },
    ],
    whoShouldTake: [
      '计划赴港发行RWA的内地企业法务、合规和战略人员',
      '香港持牌机构中负责RWA业务的合规官',
      '为RWA客户提供服务的律师事务所和会计师事务所',
      '外资机构研究进入中国RWA市场的监管与合规人员',
    ],
  },
  {
    id: 'china-rwa-path',
    coverImage: 'https://readdy.ai/api/search-image?query=China%20RWA%20path%20textbook%20cover%20dark%20crimson%20red%20Great%20Wall%20digital%20elements%20blockchain%20bridge%20China%20Hong%20Kong%20digital%20finance%20glowing%20holographic%20professional%20futuristic%20book%20cover%20design&width=600&height=840&seq=cover_cn05&orientation=portrait',
    expertQuote: '中国拥有全球最丰富的实体资产存量，而RWA提供了将这些沉睡资本与全球流动性对接的最有效路径。合规是门票，实体是根本，全球化是方向。',
    expertName: '陈文辉',
    expertTitle: '中国证监会原副主席',
    courseIntro: '在中国做RWA，需要一套独特的"中国解法"。本课程专为希望推进境内资产跨境代币化发行的企业设计，系统梳理中国监管红线、资产确权路径、QFLP/CIPS跨境通道、两链一桥技术架构，以及绿色能源、不动产、知识产权三类主流资产的完整操作手册。每个环节都配备真实操作细节与常见陷阱提示，是国内最接地气的RWA实操课程。',
    learningObjectives: [
      '清楚认识中国境内RWA的绝对禁止清单与合法发行的唯一路径',
      '掌握三类核心资产（不动产/绿色能源/知识产权）的差异化确权流程',
      '理解QFLP/CIPS两大跨境资金通道的适用场景与操作要点',
      '能够独立设计"两链一桥"技术架构方案：联盟链存证+公链发行+跨链桥',
      '了解境内监管审查流程，能够预判常见合规风险并提前规避',
    ],
    keyPoints: [
      '中国RWA监管全图：人民银行、证监会、金融监管总局三方管辖划分',
      '八部委42号文六大禁止情形逐条解析',
      '境内确权三条路径：不动产/绿能/知产的差异化流程',
      '真实出售：从境内资产到香港SPV的法律有效路径',
      'QFLP通道：合格境外有限合伙人资金入境操作细则',
      'CIPS系统：人民币跨境支付在RWA结算中的应用',
      '外汇合规：资本项目申报要求与违规风险',
      '两链一桥：联盟链（存证）+公链（发行）+跨链桥（数据同步）',
    ],
    caseStudies: [
      {
        title: 'Case Study 1：协鑫能科光伏电站RWA发行全流程',
        background: '协鑫能科于2025年将旗下内蒙古光伏电站收益权通过RWA方式在香港发行，完成境内确权存证后，通过QFLP通道引入境外机构投资者，是新政框架下国内最标准的绿色能源RWA案例之一。',
        task: '逐步骤还原协鑫能科的发行流程：能源主管部门确权→蚂蚁链存证→香港SPV设立→SFC合规审查→代币化发行→QFLP资金回流，并在每一步标注"合规关键动作"与"常见失误"。',
        discussion: '讨论：如果协鑫能科发现某台电站的发电数据有虚报，在已完成链上存证后，正确的处置方式是什么？涉及哪些法律责任？',
      },
      {
        title: 'Case Study 2：雄安新区供应链金融RWA试点解析',
        background: '雄安新区于2024年推出基于长安链的供应链金融RWA试点，核心企业信用穿透至中小供应商，应收账款上链后可直接向银行申请融资，智能合约自动触发还款，系统笔均成功率达99.7%。',
        task: '分析雄安供应链金融RWA如何解决传统供应链融资中"信息不透明、核心企业信用传递难、银行风险难以把控"三大痛点，并评估该模式向全国推广的主要障碍。',
        discussion: '分组讨论：如果你是一家中型制造业企业，核心客户是国企，应如何借助雄安模式的经验设计自己的供应链RWA融资方案？',
      },
    ],
    assignments: [
      {
        title: '中国资产RWA化可行性评估',
        type: '实战演练',
        description: '为指定资产（①河北某光伏电站，②北京某写字楼租金收益权，③某科技公司5项发明专利）中选择一个，撰写完整的RWA化可行性评估报告，包括：资产合规性评估、确权路径规划、跨境通道选择、预计成本与时间、核心风险点分析。',
        deliverable: '可行性评估报告（4页）',
      },
      {
        title: '两链一桥架构设计',
        type: '小组讨论',
        description: '三人一组，针对"碳资产RWA"场景，设计完整的"联盟链存证+以太坊公链发行+跨链桥数据同步"技术架构，明确每个组件的技术选型理由，以及数据从链下更新到链上同步的完整触发机制。',
        deliverable: '架构图 + 10分钟课堂讲解',
      },
    ],
    whoShouldTake: [
      '正在推进或计划推进资产跨境代币化的内地企业',
      '为内地企业RWA项目提供法律/财务/技术服务的服务商',
      '负责内地企业境外发行项目的投行和资产管理机构',
      '关注中国RWA市场的境外机构投资者和研究人员',
    ],
  },
  {
    id: 'carbon-rwa',
    coverImage: 'https://readdy.ai/api/search-image?query=carbon%20credit%20RWA%20tokenization%20textbook%20cover%20deep%20forest%20green%20dark%20background%20digital%20leaf%20tree%20carbon%20emission%20forest%20canopy%20blockchain%20green%20energy%20holographic%20professional%20futuristic%20book%20cover%20design&width=600&height=840&seq=cover_carbon06&orientation=portrait',
    expertQuote: 'The tokenization of carbon credits could be the single most important innovation in climate finance — creating global liquidity for what has historically been a fragmented, illiquid and opaque market.',
    expertName: 'Mark Carney',
    expertTitle: '前英格兰银行行长，联合国气候行动和融资特使',
    courseIntro: '碳资产是RWA赛道中增长最快、最具中国特色的战略性赛道。本课程系统覆盖从碳信用项目开发到链上代币化发行的全链路，重点精讲CCER/VCS两大核证标准、ERC-1155代币化技术实现、碳市场合规发行要求，以及碳资产RWA的定价与收益分配逻辑。结合中国碳市场重启、双碳政策落地的最新动态，确保你学到的是2026年真正可落地的碳资产RWA知识。',
    learningObjectives: [
      '掌握全球碳市场（ETS/CCER/VCS/自愿市场）的完整架构与交互关系',
      '能够独立开展林业碳汇/光伏减排CCER项目的方法学应用与开发评估',
      '理解碳信用代币化的技术实现路径，能够独立进行ERC选型',
      '掌握碳资产RWA在香港的合规发行要求与SFC审批要点',
      '能够设计碳资产RWA产品的完整收益分配方案',
    ],
    keyPoints: [
      '碳信用 vs 碳配额：本质差异与投资逻辑',
      'CCER重启政策解析：2023年新规与项目机会',
      'VCS/Gold Standard/CAR国际标准对比与适用场景',
      '林业碳汇项目开发全流程：调查→方法学→核查→注册',
      'MRV机制：核查机构选择标准与数据真实性保障',
      'ERC-1155 vs ERC-3525：碳资产代币化的最优选型',
      'Toucan/KlimaDAO架构分析：链上碳市场的技术实现',
      '香港SFC碳信用RWA合规要求与发行流程',
    ],
    caseStudies: [
      {
        title: 'Case Study 1：新加坡280亿美元全球最大自然资产RWA项目',
        background: '2025年，新加坡某自然资产平台完成了全球迄今最大规模的自然资产RWA项目，将涵盖东南亚6国的碳汇与生态资产代币化，总规模达280亿美元，获MAS Project Guardian框架支持。',
        task: '分析该项目的资产组合构成（碳汇/生物多样性/水资源），梳理其在新加坡监管框架下的合规发行路径，并评估其核心风险（基础资产计量/政治主权风险/流动性）及缓释策略。',
        discussion: '讨论：中国CCER项目如果参照此模式在香港发行，最主要的合规挑战是什么？应如何设计跨境合规架构？',
      },
      {
        title: 'Case Study 2：中国电力国际新能源碳资产RWA实践',
        background: '中国电力国际新能源（00735.HK）将旗下多个风电和光伏项目的CCER申请权益通过RWA方式进行预融资，解决了新能源项目开发前期资金压力，同时让投资者参与未来碳信用收益分成。',
        task: '逐步骤拆解该项目的结构：CCER申请→收益权拆分→链上代币→投资者认购→未来CCER注册→收益分配，识别每步的关键风险，并分析"CCER未能通过核查"这一极端情景下的应对机制。',
        discussion: '讨论：如果你管理一个碳资产RWA基金，如何构建包含中国CCER、欧盟ETS配额、自愿市场VCS碳信用的多元化碳资产组合？',
      },
    ],
    assignments: [
      {
        title: '碳资产RWA产品设计方案',
        type: '小组讨论',
        description: '四人一组，设计一款覆盖"项目开发-核证-代币化-二级市场"全链路的碳资产RWA产品，包括：底层碳资产类型与核证标准选择、代币化技术方案、定价与收益分配机制、投资者退出通道设计，以及主要风险揭示。',
        deliverable: '产品设计方案书（6页）+ 15分钟课堂答辩',
      },
      {
        title: '碳信用代币化研究报告',
        type: '个人作业',
        description: '选择Toucan Protocol或KlimaDAO中的一个，撰写深度研究报告，分析其技术架构、碳信用注销机制、代币经济学设计、已实现的碳减排规模，以及面临的主要质疑（漂绿风险）及应对。',
        deliverable: '研究报告（2500字）',
      },
    ],
    whoShouldTake: [
      '碳资产项目开发商和碳交易从业者',
      '新能源企业寻求碳资产增值路径的战略和融资人员',
      '绿色金融投资者和ESG基金管理人',
      '气候政策研究者和环境咨询顾问',
    ],
  },
  {
    id: 'defi-rwa-integration',
    coverImage: 'https://readdy.ai/api/search-image?query=DeFi%20RWA%20integration%20textbook%20cover%20deep%20indigo%20violet%20dark%20background%20liquidity%20pool%20AMM%20protocol%20blockchain%20yield%20farming%20digital%20asset%20streams%20glowing%20holographic%20futuristic%20professional%20book%20cover%20design&width=600&height=840&seq=cover_defi07&orientation=portrait',
    expertQuote: 'The convergence of DeFi and real-world assets represents the most exciting frontier in finance today — it is where the efficiency of programmable money meets the stability of real economic value.',
    expertName: 'Rune Christensen',
    expertTitle: 'MakerDAO创始人',
    courseIntro: 'DeFi与RWA的融合是当前金融创新最前沿的交叉地带。本课程系统讲授RWA如何接入DeFi协议，包括MakerDAO的RWA抵押机制、Aave的机构借贷市场、Centrifuge的流动性池设计，以及RWA×DeFi组合策略的收益优化与风险管理。课程同步覆盖这一领域的监管灰色地带与合规边界，确保你在追逐高收益的同时不会踩入监管陷阱。',
    learningObjectives: [
      '深度理解AMM、Lending协议、稳定币机制三大DeFi基础设施',
      '掌握RWA进入DeFi的三条主要路径及各自适用场景',
      '能够设计RWA+DeFi组合的收益优化策略，平衡收益与流动性风险',
      '理解DeFi监管趋势，识别RWA×DeFi的合规边界与红线',
      '能够分析极端市场下RWA清算机制的设计原则与失效场景',
    ],
    keyPoints: [
      'AMM原理：恒定乘积公式与无常损失的量化分析',
      '超额抵押借贷的经济学逻辑与清算触发机制',
      '稳定币三条路线：法币储备/超额抵押/算法稳定的风险对比',
      'MakerDAO RWA金库：从提案投票到资产上线的完整流程',
      'Centrifuge Tinlake：应收账款SPV接入DeFi流动性池的架构',
      'Maple Finance：无抵押机构信贷的链上信用评估机制',
      '链上利率 vs 链下利率：套利策略的设计逻辑',
      '智能合约安全审计：Reentrancy/闪电贷攻击/预言机操纵的防护',
    ],
    caseStudies: [
      {
        title: 'Case Study 1：MakerDAO RWA金库的历史演进',
        background: 'MakerDAO自2022年开始引入RWA作为DAI稳定币的抵押品，从最初的短期国债到复杂的房贷证券，RWA抵押品规模最高峰占DAI总抵押品的近50%。',
        task: '分析MakerDAO引入RWA的战略逻辑：为什么加密原生协议需要链下实体资产？梳理MakerDAO RWA抵押品的风险事件（如Huntingdon Valley Bank事件），评估其影响与教训。',
        discussion: '讨论：MakerDAO的RWA抵押品策略遭到部分社区成员强烈反对，核心矛盾是"去中心化纯粹性 vs 协议可持续收益"——你的判断是什么？',
      },
      {
        title: 'Case Study 2：Goldfinch的无抵押贷款模式',
        background: 'Goldfinch通过"社区信用评估+分级风险承担"机制，向新兴市场的中小企业提供无抵押链上融资，核心创新是"链下信用评估→链上代币化→DeFi流动性"的完整路径。',
        task: '分析Goldfinch在2022年-2023年出现的多笔贷款违约（肯尼亚、加纳等市场），评估其信用风险定价机制的失效原因，提出改进方案。',
        discussion: '讨论：如果将Goldfinch模式应用于中国农村金融（农业合作社应收账款），面临哪些本地化挑战？哪些部分可以直接借鉴，哪些需要重新设计？',
      },
    ],
    assignments: [
      {
        title: 'RWA×DeFi策略设计',
        type: '实战演练',
        description: '设计一个"以香港政府债券RWA代币为底层、接入DeFi借贷协议的收益增强策略"。要求：阐明策略逻辑、量化预期年化收益与主要风险（利率风险/流动性风险/智能合约风险）、设计风险对冲方案，并评估策略在香港监管框架下的合规性。',
        deliverable: '策略方案书（3页）+ 收益风险测算表格',
      },
      {
        title: 'DeFi协议安全审计入门',
        type: '个人作业',
        description: '阅读提供的三份开源智能合约代码片段（分别含有Reentrancy漏洞/整数溢出/预言机操纵风险），独立识别其中的安全漏洞并说明攻击向量，最后给出修复建议。',
        deliverable: '审计报告（1500字）',
      },
    ],
    whoShouldTake: [
      'DeFi从业者希望进入RWA赛道的量化分析师和策略师',
      'RWA项目方希望为代币设计DeFi接入方案的技术和产品人员',
      '机构投资者希望理解RWA在DeFi生态中的投资机会',
      '区块链安全审计工程师',
    ],
  },
  {
    id: 'international-rwa',
    coverImage: 'https://readdy.ai/api/search-image?query=global%20RWA%20regulation%20international%20markets%20textbook%20cover%20deep%20navy%20blue%20dark%20background%20earth%20globe%20digital%20network%20financial%20centers%20New%20York%20London%20Singapore%20Dubai%20holographic%20connection%20lines%20professional%20futuristic%20book%20cover%20design&width=600&height=840&seq=cover_intl08&orientation=portrait',
    expertQuote: 'The global race to establish leadership in tokenized assets is ultimately a race for regulatory clarity. The jurisdictions that provide clear rules earliest will capture the institutional capital flows that define the next decade of finance.',
    expertName: 'Gensler Gary',
    expertTitle: '美国证券交易委员会（SEC）前主席',
    courseIntro: '做跨境RWA，最大的陷阱是用一套合规逻辑应对所有市场。本课程系统对比美国（SEC/CFTC框架）、欧盟（MiCA/MiFID II）、香港（SFC体系）、新加坡（MAS）、中东（DIFC/ADGM）五大核心司法辖区的RWA监管逻辑，帮助你为不同资产、不同投资者群体选择最优的合规落地方案，并规避跨境监管套利的法律风险。',
    learningObjectives: [
      '深度理解五大司法辖区RWA监管框架的逻辑基础与差异来源',
      '掌握美国Reg D/A+/S三大豁免路径的适用条件与操作要点',
      '能够分析欧盟MiCA条例对不同类型数字资产的分类与监管要求',
      '理解MAS Project Guardian框架与新加坡RWA创新监管模式',
      '能够为跨司法辖区的RWA项目设计"最优合规路径"决策框架',
    ],
    keyPoints: [
      'Howey Test：美国证券认定标准在RWA中的应用边界',
      'Reg D 506(c)：合格投资者豁免发行的全套操作细节',
      'Reg S：向非美投资者销售的合规安排与营销限制',
      'MiCA分类：资产参照代币vs电子货币代币vs其他加密资产',
      'MiFID II DLT MTF：欧盟证券型RWA的交易场所监管',
      'MAS Project Guardian：新加坡开放式创新监管实验',
      'DIFC/ADGM：中东金融中心的RWA战略与监管框架',
      '跨境合规决策树：资产类型×投资者类型×发行人注册地的选择矩阵',
    ],
    caseStudies: [
      {
        title: 'Case Study 1：Ondo Finance的全球合规布局',
        background: 'Ondo Finance是美国最大的代币化国债平台之一（OUSG等产品），面向全球机构投资者，同时需应对美国SEC、欧盟MiCA、香港SFC等多个司法辖区的合规要求。',
        task: '分析Ondo如何在"美国合规限制（仅限合格投资者）+ 欧盟统一发行路径 + 亚太市场开放性"三者之间做出取舍，绘制其不同地区的合规架构差异图。',
        discussion: '讨论：如果Ondo的OUSG产品要向中国香港的专业投资者开放，需要满足哪些额外的SFC要求？',
      },
      {
        title: 'Case Study 2：迪拜DIFC对RWA的战略布局',
        background: '迪拜国际金融中心（DIFC）于2024年推出专项RWA监管框架，大力吸引全球RWA项目落地，已吸引超过30家RWA平台在DIFC注册，其中包括多个以GCC（海湾合作委员会）资产为底层的项目。',
        task: '对比DIFC框架与香港SFC框架在"投资者准入门槛、底层资产要求、二级市场流通规则"三个维度的差异，分析哪类中国资产更适合选择DIFC作为发行地。',
        discussion: '分组讨论：如果一家中东家族办公室希望将其持有的沙特阿拉伯商业地产通过RWA代币化，向亚洲机构投资者募资，应该选择哪个司法辖区？你的理由是什么？',
      },
    ],
    assignments: [
      {
        title: '跨境RWA合规方案设计',
        type: '分组报告',
        description: '四人一组，为以下场景设计跨境合规方案："一个中国内地房企，希望将其位于上海的商业写字楼收益权，通过RWA方式向美国机构投资者和欧洲高净值人士募资。"方案需涵盖：发行地选择及理由、各目标市场的合规路径、投资者适当性管理方案、信息披露标准、以及合规成本与时间估算。',
        deliverable: 'PPT演示（20分钟）+ 合规方案摘要（2页）',
      },
      {
        title: '全球RWA监管对比分析',
        type: '个人作业',
        description: '从本课程涵盖的5个司法辖区中选择2个，撰写深度对比分析，重点分析两个辖区在"证券型数字资产认定、发行主体资质要求、二级市场监管"三个维度的异同，并给出你对"全球RWA监管最终走向统一还是碎片化"的判断与依据。',
        deliverable: '对比分析报告（2500字）',
      },
    ],
    whoShouldTake: [
      '需要管理跨司法辖区合规风险的RWA项目方',
      '跨国投行和法律机构的数字资产业务人员',
      '政策研究者和各国监管机构',
      '希望在全球市场布局RWA的机构投资者',
    ],
  },
];

export interface LocalizedModule {
  title: string;
  lessons: string[];
}

// Localized module data for each course and language
// Structure: courseId -> langCode -> modules[]
export const courseModulesLocalized: Record<string, Record<string, LocalizedModule[]>> = {
  'bc-fundamentals': {
    zh: [], // will fall back to COURSES data
    en: [
      {
        title: 'Module 1: What Is Blockchain — The Essence of Distributed Trust',
        lessons: [
          'Why Do We Need Blockchain? The Limitations of Centralized Trust',
          'Blockchain vs. Traditional Database: The Fundamental Difference',
          'The Three Core Properties: Decentralization, Immutability, Transparency',
          'Public Chain vs. Private Chain vs. Consortium Chain: When to Use Which',
          'Real-World Analogy: Blockchain as a "Global Notary Office"',
        ],
      },
      {
        title: 'Module 2: Cryptography Fundamentals — The Mathematical Foundation of Blockchain',
        lessons: [
          'Hash Functions: The "Digital Fingerprint" of Blockchain',
          'SHA-256 vs. Keccak-256: Bitcoin vs. Ethereum Hash Algorithms',
          'Asymmetric Encryption: Public Key, Private Key, and Digital Signatures',
          'Merkle Tree: Efficient Data Integrity Verification',
          'Zero-Knowledge Proofs: Privacy-Preserving Verification',
        ],
      },
      {
        title: 'Module 3: Consensus Mechanisms — The "Democratic Decision System" of Blockchain',
        lessons: [
          'PoW (Proof of Work): How Bitcoin Mining Works',
          'PoS (Proof of Stake): Ethereum\'s Energy-Efficient Alternative',
          'PBFT: The Consensus Mechanism of Consortium Chains',
          'The Blockchain Trilemma: Security, Decentralization, Scalability',
          'Consensus Mechanism Selection for RWA: Public Chain vs. Consortium Chain',
        ],
      },
      {
        title: 'Module 4: EVM and Smart Contracts — The "Automatic Judge" of Blockchain',
        lessons: [
          'What Is the Ethereum Virtual Machine (EVM)?',
          'Smart Contract Execution Logic: Conditions, Triggers, and Automation',
          'Solidity Basics: Reading Smart Contract Code',
          'Gas Economics: Incentive Mechanisms and Fee Models',
          'Smart Contract Security Audits: Why They Are Mandatory for RWA',
        ],
      },
      {
        title: 'Module 5: Consortium Chains in China — The Compliant Blockchain Path',
        lessons: [
          'Why China Uses Consortium Chains Instead of Public Chains',
          'AntChain, Changan Chain, FISCO BCOS: A Comparison',
          'BSN (Blockchain Service Network): National Infrastructure',
          'Blockchain Information Service Registration: Compliance Requirements',
          'Consortium Chain RWA Applications: Supply Chain Finance Case Studies',
        ],
      },
      {
        title: 'Module 6: Cross-Chain and Layer 2 — Solving Scalability',
        lessons: [
          'Why Cross-Chain? The Interoperability Problem',
          'Bridge Mechanisms: Lock-and-Mint vs. Native Bridges',
          'Layer 2 Solutions: Optimism, Arbitrum, Polygon',
          'HashKey Chain: Hong Kong\'s Compliant L2 for RWA',
          'CCIP: Chainlink\'s Cross-Chain Interoperability Protocol',
        ],
      },
    ],
    fr: [
      {
        title: 'Module 1 : Qu\'est-ce que la Blockchain — L\'Essence de la Confiance Distribuée',
        lessons: [
          'Pourquoi Avons-Nous Besoin de la Blockchain ? Les Limites de la Confiance Centralisée',
          'Blockchain vs. Base de Données Traditionnelle : La Différence Fondamentale',
          'Les Trois Propriétés Fondamentales : Décentralisation, Immuabilité, Transparence',
          'Chaîne Publique vs. Privée vs. Consortium : Quand Utiliser Laquelle',
          'Analogie Réelle : La Blockchain comme "Office Notarial Mondial"',
        ],
      },
      {
        title: 'Module 2 : Fondamentaux de la Cryptographie — La Base Mathématique',
        lessons: [
          'Fonctions de Hachage : L\'"Empreinte Digitale" de la Blockchain',
          'SHA-256 vs. Keccak-256 : Algorithmes Bitcoin vs. Ethereum',
          'Chiffrement Asymétrique : Clé Publique, Clé Privée et Signatures Numériques',
          'Arbre de Merkle : Vérification Efficace de l\'Intégrité des Données',
          'Preuves à Connaissance Nulle : Vérification Préservant la Confidentialité',
        ],
      },
      {
        title: 'Module 3 : Mécanismes de Consensus — Le "Système de Décision Démocratique"',
        lessons: [
          'PoW (Preuve de Travail) : Comment Fonctionne le Minage Bitcoin',
          'PoS (Preuve d\'Enjeu) : L\'Alternative Économe en Énergie d\'Ethereum',
          'PBFT : Le Mécanisme de Consensus des Chaînes Consortium',
          'Le Trilemme Blockchain : Sécurité, Décentralisation, Évolutivité',
          'Sélection du Mécanisme de Consensus pour RWA',
        ],
      },
      {
        title: 'Module 4 : EVM et Contrats Intelligents — Le "Juge Automatique"',
        lessons: [
          'Qu\'est-ce que la Machine Virtuelle Ethereum (EVM) ?',
          'Logique d\'Exécution des Contrats Intelligents',
          'Bases de Solidity : Lire le Code des Contrats Intelligents',
          'Économie du Gas : Mécanismes d\'Incitation et Modèles de Frais',
          'Audits de Sécurité des Contrats Intelligents pour RWA',
        ],
      },
      {
        title: 'Module 5 : Chaînes Consortium en Chine — La Voie Blockchain Conforme',
        lessons: [
          'Pourquoi la Chine Utilise des Chaînes Consortium',
          'AntChain, Changan Chain, FISCO BCOS : Comparaison',
          'BSN : Infrastructure Nationale Blockchain',
          'Enregistrement des Services d\'Information Blockchain',
          'Applications RWA sur Chaîne Consortium : Études de Cas',
        ],
      },
      {
        title: 'Module 6 : Cross-Chain et Layer 2 — Résoudre l\'Évolutivité',
        lessons: [
          'Pourquoi le Cross-Chain ? Le Problème d\'Interopérabilité',
          'Mécanismes de Bridge : Lock-and-Mint vs. Bridges Natifs',
          'Solutions Layer 2 : Optimism, Arbitrum, Polygon',
          'HashKey Chain : L2 Conforme de Hong Kong pour RWA',
          'CCIP : Protocole d\'Interopérabilité Cross-Chain de Chainlink',
        ],
      },
    ],
    ja: [
      {
        title: 'モジュール1：ブロックチェーンとは — 分散型信頼の本質',
        lessons: [
          'なぜブロックチェーンが必要か？中央集権的信頼の限界',
          'ブロックチェーン vs 従来のデータベース：根本的な違い',
          '3つのコア特性：分散化、不変性、透明性',
          'パブリックチェーン vs プライベートチェーン vs コンソーシアムチェーン',
          '現実の比喩：「グローバル公証役場」としてのブロックチェーン',
        ],
      },
      {
        title: 'モジュール2：暗号学の基礎 — ブロックチェーンの数学的基盤',
        lessons: [
          'ハッシュ関数：ブロックチェーンの「デジタル指紋」',
          'SHA-256 vs Keccak-256：ビットコイン vs イーサリアム',
          '非対称暗号化：公開鍵、秘密鍵、デジタル署名',
          'マークルツリー：効率的なデータ整合性検証',
          'ゼロ知識証明：プライバシー保護型検証',
        ],
      },
      {
        title: 'モジュール3：コンセンサスメカニズム — ブロックチェーンの「民主的意思決定」',
        lessons: [
          'PoW（プルーフ・オブ・ワーク）：ビットコインマイニングの仕組み',
          'PoS（プルーフ・オブ・ステーク）：イーサリアムの省エネ代替案',
          'PBFT：コンソーシアムチェーンのコンセンサスメカニズム',
          'ブロックチェーントリレンマ：セキュリティ、分散化、スケーラビリティ',
          'RWAのためのコンセンサスメカニズム選択',
        ],
      },
      {
        title: 'モジュール4：EVMとスマートコントラクト — ブロックチェーンの「自動裁判官」',
        lessons: [
          'イーサリアム仮想マシン（EVM）とは？',
          'スマートコントラクトの実行ロジック',
          'Solidityの基礎：スマートコントラクトコードの読み方',
          'Gasエコノミクス：インセンティブメカニズムと手数料モデル',
          'RWAのためのスマートコントラクトセキュリティ監査',
        ],
      },
      {
        title: 'モジュール5：中国のコンソーシアムチェーン — コンプライアントなブロックチェーンの道',
        lessons: [
          'なぜ中国はコンソーシアムチェーンを使うのか',
          'アントチェーン、長安チェーン、FISCO BCOS：比較',
          'BSN：国家ブロックチェーンインフラ',
          'ブロックチェーン情報サービス登録：コンプライアンス要件',
          'コンソーシアムチェーンRWAアプリケーション：サプライチェーン金融',
        ],
      },
      {
        title: 'モジュール6：クロスチェーンとLayer 2 — スケーラビリティの解決',
        lessons: [
          'なぜクロスチェーンが必要か？相互運用性の問題',
          'ブリッジメカニズム：ロックアンドミント vs ネイティブブリッジ',
          'Layer 2ソリューション：Optimism、Arbitrum、Polygon',
          'HashKey Chain：RWA向け香港のコンプライアントL2',
          'CCIP：ChainlinkのクロスチェーンInteroperabilityプロトコル',
        ],
      },
    ],
    de: [
      {
        title: 'Modul 1: Was ist Blockchain — Das Wesen des verteilten Vertrauens',
        lessons: [
          'Warum brauchen wir Blockchain? Die Grenzen zentralisierter Vertrauensmodelle',
          'Blockchain vs. Traditionelle Datenbank: Der grundlegende Unterschied',
          'Die drei Kerneigenschaften: Dezentralisierung, Unveränderlichkeit, Transparenz',
          'Cadeia Pública vs. Privada vs. Consórcio: Wenn welche verwenden',
          'Analogie real: Blockchain als "Globales Notariat"',
        ],
      },
      {
        title: 'Modul 2: Fundamentale Kryptographie — Die mathematische Basis',
        lessons: [
          'Hash-Funktionen: Der "digitale Fingerabdruck" der Blockchain',
          'SHA-256 vs. Keccak-256: Algoritmen Bitcoin vs. Ethereum',
          'Asymmetrische Verschlüsselung: Öffentlicher Schlüssel, Privater Schlüssel, Digitale Signaturen',
          'Merkle-Baum: Effiziente Datenintegritätsprüfung',
          'Zero-Knowledge-Beweise: Verifikation, die die Privatsphäre schützt',
        ],
      },
      {
        title: 'Modul 3: Konsensmechanismen — Das "demokratische Entscheidungssystem"',
        lessons: [
          'PoW (Proof of Work): Wie Bitcoin-Mining funktioniert',
          'PoS (Proof of Stake): Ethereums energieeffiziente Alternative',
          'PBFT: Der Konsensmechanismus von Consortium Chains',
          'Das Blockchain-Trilemma: Sicherheit, Desentralisation, Skalierbarkeit',
          'Selektion des Konsensmechanismus für RWA',
        ],
      },
      {
        title: 'Modul 4: EVM und Smart Contracts — Der "automatische Richter"',
        lessons: [
          'Was ist die Ethereum Virtual Machine (EVM)?',
          'Lógica de ejecución de contratos inteligentes',
          'Conceptos básicas de Solidity: Leer código de contratos inteligentes',
          'Economía del Gas: Mecanismos de incentivo y modelos de tarifas',
          'Auditorías de seguridad de contratos inteligentes para RWA',
        ],
      },
      {
        title: 'Modul 5: Cadeias de Consórcio en China — El Camino Blockchain Conforme',
        lessons: [
          'Por qué China usa cadenas de consórcio en lugar de cadenas públicas',
          'AntChain, Changan Chain, FISCO BCOS: Una comparación',
          'BSN: Infraestrutura Blockchain Nacional',
          'Registro de Servicios de Información Blockchain',
          'Aplicaciones RWA en cadenas de consórcio: Finanças da cadeia de suprimentos',
        ],
      },
      {
        title: 'Modul 6: Cross-Chain y Layer 2 — Resolviendo la Escalabilidad',
        lessons: [
          'Por qué Cross-Chain? El problema de interoperabilidad',
          'Mecanismos de Bridge: Lock-and-Mint vs. Bridges Nativos',
          'Soluciones Layer 2: Optimism, Arbitrum, Polygon',
          'HashKey Chain: L2 conforme de Hong Kong para RWA',
          'CCIP: Protocolo de Interoperabilidad Cross-Chain de Chainlink',
        ],
      },
    ],
    es: [
      {
        title: 'Módulo 1: ¿Qué es Blockchain? — La Esencia de la Confianza Distribuida',
        lessons: [
          '¿Por qué necesitamos Blockchain? Las limitaciones de la confianza centralizada',
          'Blockchain vs. Base de Datos Tradicional: La diferencia fundamental',
          'Las tres propiedades fundamentales: Descentralización, Inmutabilidad, Transparencia',
          'Cadeia Pública vs. Privada vs. Consórcio: Cuando usar cuál',
          'Analogía real: Blockchain como "Notaría Global"',
        ],
      },
      {
        title: 'Módulo 2: Fundamentos de Criptografía — La Base Matemática',
        lessons: [
          'Funciones Hash: La "Huella Digital" de Blockchain',
          'SHA-256 vs. Keccak-256: Algoritmos Bitcoin vs. Ethereum',
          'Criptografía Assimétrica: Clave Pública, Clave Privada y Assinaturas Digitales',
          'Árbol de Merkle: Verificación Eficiente de Integridad de Datos',
          'Pruebas de Conocimiento Zero: Verificación que Preserva la Privacidad',
        ],
      },
      {
        title: 'Módulo 3: Mecanismos de Consenso — El "Sistema de Decisión Democrática"',
        lessons: [
          'PoW (Prova de Trabalho): Cómo funciona la minería de Bitcoin',
          'PoS (Prova de Participación): La alternativa energéticamente eficiente de Ethereum',
          'PBFT: El mecanismo de consenso de las cadenas de consórcio',
          'El Trilema Blockchain: Seguridad, Descentralización, Escalabilidad',
          'Selección del mecanismo de consenso para RWA',
        ],
      },
      {
        title: 'Módulo 4: EVM y Contratos Inteligentes — El "Juiz Automático"',
        lessons: [
          '¿Qué es la Máquina Virtual de Ethereum (EVM)?',
          'Lógica de ejecución de contratos inteligentes',
          'Noiones básicas de Solidity: Leer código de contratos inteligentes',
          'Economía del Gas: Mecanismos de incentivo y modelos de tarifas',
          'Auditorías de seguridad de contratos inteligentes para RWA',
        ],
      },
      {
        title: 'Módulo 5: Cadeias de Consórcio en China — El Camino Blockchain Conforme',
        lessons: [
          'Por qué China usa cadenas de consórcio en lugar de cadenas públicas',
          'AntChain, Changan Chain, FISCO BCOS: Una comparación',
          'BSN: Infraestrutura Blockchain Nacional',
          'Registro de Servicios de Información Blockchain',
          'Aplicaciones RWA en cadenas de consórcio: Finanças da cadeia de suprimentos',
        ],
      },
      {
        title: 'Módulo 6: Cross-Chain y Layer 2 — Resolviendo la Escalabilidad',
        lessons: [
          'Por qué Cross-Chain? El problema de interoperabilidad',
          'Mecanismos de Bridge: Lock-and-Mint vs. Bridges Nativos',
          'Soluciones Layer 2: Optimism, Arbitrum, Polygon',
          'HashKey Chain: L2 conforme de Hong Kong para RWA',
          'CCIP: Protocolo de Interoperabilidad Cross-Chain de Chainlink',
        ],
      },
    ],
    pt: [
      {
        title: 'Módulo 1: O que é Blockchain — A Essência da Confiança Distribuída',
        lessons: [
          'Por que precisamos de Blockchain? As limitações da confiança centralizada',
          'Blockchain vs. Banco de Dados Tradicional: A diferença fundamental',
          'As três propriedades fundamentais: Descentralização, Imutabilidade, Transparência',
          'Cadeia Pública vs. Privada vs. Consórcio: Quando usar qual',
          'Analogia real: Blockchain como "Notaría Global"',
        ],
      },
      {
        title: 'Módulo 2: Fundamentos de Criptografia — A Base Matemática',
        lessons: [
          'Funções Hash: A "Impressão Digital" do Blockchain',
          'SHA-256 vs. Keccak-256: Algoritmos Bitcoin vs. Ethereum',
          'Criptografia Assimétrica: Chave Pública, Chave Privada e Assinaturas Digitais',
          'Árvore de Merkle: Verificação Eficiente de Integridade de Dados',
          'Provas de Conhecimento Zero: Verificação que Preserva a Privacidade',
        ],
      },
      {
        title: 'Módulo 3: Mecanismos de Consenso — O "Sistema de Decisão Democrática"',
        lessons: [
          'PoW (Prova de Trabalho): Cómo funciona la minería de Bitcoin',
          'PoS (Prova de Participación): La alternativa energéticamente eficiente de Ethereum',
          'PBFT: El mecanismo de consenso de las cadenas de consórcio',
          'El Trilema Blockchain: Seguridad, Descentralización, Escalabilidad',
          'Seleção do mecanismo de consenso para RWA',
        ],
      },
      {
        title: 'Módulo 4: EVM e Contratos Inteligentes — O "Juiz Automático"',
        lessons: [
          '¿Qué es la Máquina Virtual de Ethereum (EVM)?',
          'Lógica de ejecución de contratos inteligentes',
          'Conceptos básicas de Solidity: Leer código de contratos inteligentes',
          'Economía del Gas: Mecanismos de incentivo y modelos de tarifas',
          'Auditorías de seguridad de contratos inteligentes para RWA',
        ],
      },
      {
        title: 'Módulo 5: Cadeias de Consórcio en China — El Camino Blockchain Conforme',
        lessons: [
          'Por qué China usa cadenas de consórcio en lugar de cadenas públicas',
          'AntChain, Changan Chain, FISCO BCOS: Uma comparação',
          'BSN: Infraestrutura Blockchain Nacional',
          'Registro de Serviços de Informação Blockchain',
          'Aplicaciones RWA en cadenas de consórcio: Finanças da cadeia de suprimentos',
        ],
      },
      {
        title: 'Módulo 6: Cross-Chain y Layer 2 — Resolviendo la Escalabilidad',
        lessons: [
          'Por qué Cross-Chain? El problema de interoperabilidad',
          'Mecanismos de Bridge: Lock-and-Mint vs. Bridges Nativos',
          'Soluciones Layer 2: Optimism, Arbitrum, Polygon',
          'HashKey Chain: L2 conforme de Hong Kong para RWA',
          'CCIP: Protocolo de Interoperabilidad Cross-Chain de Chainlink',
        ],
      },
    ],
    ko: [
      {
        title: '모듈 1: 블록체인이란 — 분산 신뢰의 본질',
        lessons: [
          '왜 블록체인이 필요한가? 중앙집권적 신뢰의 한계',
          '블록체인 vs 전통 데이터베이스: 근본적인 차이',
          '세 가지 핵심 특성: 탈중앙화, 불변성, 투명성',
          '퍼블릭 체인 vs 프라이빗 체인 vs 컨소시엄 체인',
          '현실 비유: "글로벌 공증 사무소"로서의 블록체인',
        ],
      },
      {
        title: '모듈 2: 암호학 기초 — 블록체인의 수학적 기반',
        lessons: [
          '해시 함수: 블록체인의 "디지털 지문"',
          'SHA-256 vs Keccak-256: 비트코인 vs 이더리움',
          '비대칭 암호화: 공개키, 개인키, 디지털 서명',
          '머클 트리: 효율적인 데이터 무결성 검증',
          '영지식 증명: 프라이버시 보존 검증',
        ],
      },
      {
        title: '모듈 3: 합의 메커니즘 — 블록체인의 "민주적 의사결정 시스템"',
        lessons: [
          'PoW (작업 증명): 비트코인 채굴 작동 방식',
          'PoS (지분 증명): 이더리움의 에너지 효율적 대안',
          'PBFT: 컨소시엄 체인의 합의 메커니즘',
          '블록체인 트릴레마: 보안, 탈중앙화, 확장성',
          'RWA를 위한 합의 메커니즘 선택',
        ],
      },
      {
        title: '모듈 4: EVM과 스마트 컨트랙트 — 블록체인의 "자동 판사"',
        lessons: [
          '이더리움 가상 머신(EVM)이란?',
          '스마트 컨트랙트 실행 로직',
          'Solidity 기초: 스마트 컨트랙트 코드 읽기',
          'Gas 경제학: 인센티브 메커니즘과 수수료 모델',
          'RWA를 위한 스마트 컨트랙트 보안 감사',
        ],
      },
      {
        title: '모듈 5: Cadeias de Consórcio en China — El Camino Blockchain Conforme',
        lessons: [
          '왜 China는 퍼블릭 체인 대신 컨소시엄 체인을 사용하는가',
          'AntChain, Changan Chain, FISCO BCOS: 비교',
          'BSN: 국가 블록체인 인프라',
          '블록체인 정보 서비스 등록: 규정 준수 요건',
          '컨소시엄 체인 RWA 응용: 공급망 금융 사례',
        ],
      },
      {
        title: '모듈 6: Cross-Chain y Layer 2 — Resolviendo la Escalabilidad',
        lessons: [
          'Por qué Cross-Chain? El problema de interoperabilidad',
          'Mecanismos de Bridge: Lock-and-Mint vs. Bridges Nativos',
          'Soluciones Layer 2: Optimism, Arbitrum, Polygon',
          'HashKey Chain: L2 conforme de Hong Kong para RWA',
          'CCIP: Protocolo de Interoperabilidad Cross-Chain de Chainlink',
        ],
      },
    ],
    ar: [
      {
        title: 'الوحدة 1: ما هو البلوكتشين — جوهر الثقة الموزعة',
        lessons: [
          'لماذا نحتاج البلوكتشين؟ حدود الثقة المركزية',
          'البلوكتشين مقابل قاعدة البيانات التقليدية: الفرق الجوهري',
          'الخصائص الثلاث الأساسية: اللامركزية، الثبات، الشفافية',
          'السلسلة العامة مقابل الخاصة مقابل الاتحادية',
          'تشبيه واقعي: البلوكتشين كـ"مكتب توثيق عالمي"',
        ],
      },
      {
        title: 'الوحدة 2: أساسيات التشفير — الأساس الرياضي للبلوكتشين',
        lessons: [
          'دوال التجزئة: "البصمة الرقمية" للبلوكتشين',
          'SHA-256 مقابل Keccak-256: خوارزميات بيتكوين وإيثيريوم',
          'التشفير غير المتماثل: المفتاح العام والخاص والتوقيعات الرقمية',
          'شجرة ميركل: التحقق الفعال من سلامة البيانات',
          'إثباتات المعرفة الصفرية: التحقق مع الحفاظ على الخصوصية',
        ],
      },
      {
        title: 'الوحدة 3: آليات التوافق — "نظام القرار الديمقراطي"',
        lessons: [
          'PoW (إثبات العمل): كيف يعمل تعدين البيتكوين',
          'PoS (إثبات الحصة): البديل الموفر للطاقة من إيثيريوم',
          'PBFT: آلية توافق سلاسل الاتحاد',
          'ثلاثية البلوكتشين: الأمان، اللامركزية، قابلية التوسع',
          'اختيار آلية التوافق لـ RWA',
        ],
      },
      {
        title: 'الوحدة 4: EVM والعقود الذكية — "القاضي التلقائي"',
        lessons: [
          'ما هي الآلة الافتراضية لإيثيريوم (EVM)?',
          'منطق تنفيذ العقود الذكية',
          'أساسيات Solidity: قراءة كود العقود الذكية',
          'Экономика Gas: آليات الحوافز ونماذج الرسوم',
          'تدقيق أمان العقود الذكية لـ RWA',
        ],
      },
      {
        title: 'الوحدة 5: سلاسل الاتحاد في الصين — مسار البلوكتشين المتوافق',
        lessons: [
          'لماذا تستخدم الصين سلاسل الاتحاد بدلاً من السلاسل العامة',
          'AntChain وChangan Chain وFISCO BCOS: مقارنة',
          'BSN: البنية التحتية الوطنية للبلوكتشين',
          'تسجيل خدمات معلومات البلوكتشين',
          'تطبيقات RWA على سلاسل الاتحاد: تمويل سلسلة التوريد',
        ],
      },
      {
        title: 'الوحدة 6: Cross-Chain وLayer 2 — حل قابلية التوسع',
        lessons: [
          'لماذا Cross-Chain؟ مشكلة التشغيل البيني',
          'آليات Bridge: Lock-and-Mint مقابل Bridges الأصلية',
          'Soluciones Layer 2: Optimism, Arbitrum, Polygon',
          'HashKey Chain: L2 المتوافق في هونغ كونغ لـ RWA',
          'CCIP: بروتوكول التشغيل البيني Cross-Chain من Chainlink',
        ],
      },
    ],
    ru: [
      {
        title: 'Модуль 1: Что такое Блокчейн — Суть Распределённого Доверия',
        lessons: [
          'Зачем нужен Блокчейн? Ограничения Централизованного Доверия',
          'Блокчейн vs Традиционная База Данных: Фундаментальное Различие',
          'Три Ключевых Свойства: Децентрализация, Неизменность, Прозрачность',
          'Публичная vs Частная vs Консорциумная Цепочка',
          'Реальная Аналогия: Блокчейн как "Глобальная Нотариальная Контора"',
        ],
      },
      {
        title: 'Модуль 2: Основы Криптографии — Математическая Основа Блокчейна',
        lessons: [
          'Хеш-функции: "Цифровой Отпечаток" Блокчейна',
          'SHA-256 vs. Keccak-256: Алгоритмы Bitcoin vs Ethereum',
          'Асимметричное Шифрование: Публичный Ключ, Приватный Ключ, Цифровые Подписи',
          'Дерево Меркла: Эффективная Проверка Целостности Данных',
          'Доказательства с Нулевым Разглашением: Верификация с Сохранением Конфиденциальности',
        ],
      },
      {
        title: 'Модуль 3: Механизмы Консенсуса — "Демократическая Система Принятия Решений"',
        lessons: [
          'PoW (Доказательство работы): Как работает майнинг Bitcoin',
          'PoS (Доказательство доли): Энергоэффективная альтернатива Ethereum',
          'PBFT: Механизм консенсуса консорциумных цепочек',
          'Трилемма Блокчейна: Безопасность, Децентрализация, Масштабируемость',
          'Выбор механизма консенсуса для RWA',
        ],
      },
      {
        title: 'Модуль 4: EVM и Смарт-контракты — "Автоматический Судья"',
        lessons: [
          'Что такое Виртуальная Машина Ethereum (EVM)?',
          'Логика выполнения смарт-контрактов',
          'Основы Solidity: Чтение кода смарт-контрактов',
          'Экономика Gas: Механизмы стимулирования и модели комиссий',
          'Аудит безопасности смарт-контрактов для RWA',
        ],
      },
      {
        title: 'Модуль 5: Консорциумные Цепочки в Китае — Соответствующий Блокчейн-путь',
        lessons: [
          'Почему Китай использует консорциумные цепочки вместо публичных',
          'AntChain, Changan Chain, FISCO BCOS: Сравнение',
          'BSN: Национальная Блокчейн-инфраструктура',
          'Регистрация Сервисов Блокчейн-информации',
          'Применение RWA на консорциумных цепочках: Финансирование цепочки поставок',
        ],
      },
      {
        title: 'Модуль 6: Cross-Chain и Layer 2 — Решение Масштабируемости',
        lessons: [
          'Зачем Cross-Chain? Проблема Интероперабельности',
          'Механизмы Bridge: Lock-and-Mint vs. Native Bridges',
          'Решения Layer 2: Optimism, Arbitrum, Polygon',
          'HashKey Chain: Соответствующий L2 Гонконга для RWA',
          'CCIP: Протокол Cross-Chain Интероперабельности Chainlink',
        ],
      },
    ],
  },
};
